import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { adminLogin } from '../../api/axios';
import { useAdminAuthStore } from '../../store/authStore';

interface LoginForm {
  username: string;
  password: string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { updateSession } = useAdminAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    try {
      const session = await adminLogin(data.username, data.password);
      updateSession(session);
      navigate('/');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Authentication failed';
      setError(msg);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0F0F14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(138,112,214,0.08) 0%, transparent 70%)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <motion.div
          animate={shake ? { x: [-8, 8, -6, 6, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Paper
            sx={{
              width: 420,
              p: 4,
              border: '1px solid rgba(255,255,255,0.08)',
              bgcolor: '#1A1B23',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #8A70D6 0%, #6A50B6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: '0 8px 24px rgba(138,112,214,0.35)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 800,
                    color: '#fff',
                    fontFamily: '"Poppins", sans-serif',
                    letterSpacing: '-0.02em',
                  }}
                >
                  SM
                </Typography>
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}
              >
                SkolarMinds
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  mt: 0.25,
                }}
              >
                Admin Console
              </Typography>
            </Box>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ mb: 2.5, fontSize: '0.8rem' }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Username or Email"
                  autoComplete="username"
                  autoFocus
                  error={!!errors.username}
                  helperText={errors.username?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <User size={15} style={{ color: '#64748B' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register('username', { required: 'Username is required' })}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock size={15} style={{ color: '#64748B' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowPassword((s) => !s)}
                            edge="end"
                            tabIndex={-1}
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...register('password', { required: 'Password is required' })}
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.25,
                  fontSize: '0.875rem',
                  background: 'linear-gradient(135deg, #8A70D6 0%, #6A50B6 100%)',
                  boxShadow: '0 4px 16px rgba(138,112,214,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9B82E0 0%, #7B62C0 100%)',
                    boxShadow: '0 6px 20px rgba(138,112,214,0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 150ms ease',
                }}
              >
                {isSubmitting ? 'Signing in…' : 'Sign in to Admin Console'}
              </Button>
            </Box>

            {/* Footer note */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                mt: 3,
                color: 'text.disabled',
                fontSize: '0.65rem',
              }}
            >
              Access restricted to SystemAdmin role only
            </Typography>
          </Paper>
        </motion.div>
      </motion.div>
    </Box>
  );
}
